import { Injectable, signal } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { createClient } from '@workos-inc/authkit-js';
import { Observable, from, catchError } from 'rxjs';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';
import { SlackService } from './slack.service';
import { NetworkService } from './network.service'
import { Events } from './Events'

type Client = Awaited<ReturnType<typeof createClient>>;

@Injectable({
  providedIn: 'root',
})
export class WorkosService {
  private authkit: Client | null = null;
  private isInitialized: Promise<void>;
  private _isInitializedSignal = signal(false);
  public isInitializedSignal = this._isInitializedSignal.asReadonly();

  constructor(
    private storageService: StorageService,
    private slackService: SlackService,
    private networkService: NetworkService,
    private events: Events,
  ) {
    let resolveInitializationPromise: () => void;
    this.isInitialized = new Promise<void>((resolve) => {
      resolveInitializationPromise = resolve;
    });
    this.networkService.runWhenOnline(async () => {
      try {
        await this.initAuthkit();
      } finally {
        resolveInitializationPromise();
        this._isInitializedSignal.set(true);
      }
    });
  }

  private async initAuthkit(mode: 'normal' | 'signIn' = 'normal') {
    const devMode = true;
    // dev mode must be on in order to preserve the connection on the mobile app.
    // It must also be true for the PWA because the legacy app PWA is not on the same domain than
    // our WorkOS custom domain (*.bepark.eu vs *.izix.eu)
    // Explanation on Notion https://www.notion.so/izix-belgium/Technical-notes-1de0bfb233d18085b290f2a2bf7520ec

    // We need to define a different redirectUri when signing in from the native app, as the redirect URI is hosted on
    // the platform domain and then deep linked to the app.
    const redirectUri =
      Capacitor.isNativePlatform() && mode === 'signIn'
        ? environment.nativeOAuthRedirectUri
        : environment.pwaOAuthRedirectUri;

    const isRefreshTokenAvailable = this.storageService.getWorkosRefreshToken() != null && this.storageService.getWorkosRefreshToken() != "";

    try {
      this.authkit = await createClient(environment.workosClientId, { redirectUri, devMode });
      if (isRefreshTokenAvailable) void this.checkRefreshToken();
    } catch (error) {
      console.error('WorkOS initialization error', error);
      void this.slackService.postError(error, 'WorkOS initialization error', null, 'critical');
    }

    void this.publishAuthEvent();
  }

  private async publishAuthEvent() {
    if (await this.isAuthenticated()) {
      this.events.publish('signed_in');
    } else {
      this.events.publish('signed_out')
    }
  }

  private async checkRefreshToken() {
    const isAuthenticated = await this.isAuthenticated();
    if (isAuthenticated) return;
    
    const isRefreshTokenAvailable = this.storageService.getWorkosRefreshToken() != null && this.storageService.getWorkosRefreshToken() != "";
    if (isRefreshTokenAvailable) return;

    let canGetToken: boolean;
    try { 
      await this.getAccessToken();
      canGetToken = true;
    } catch {
      canGetToken = false;
    }

    await this.slackService.postError(
      { 
        message: 'Authkit cleared the refresh token during its initialization',
        canGetToken
      },
      'Refresh token cleared by WorkOS',
      null,
      'critical'
    )
  }

  public async getSignInUrl() {
    await this.initAuthkit('signIn');
    return await this.authkit.getSignInUrl();
  }

  public async finishOAuthFlow() {
    await this.initAuthkit();
  }

  public async isAuthenticated(): Promise<boolean> {
    try {
      await this.isInitialized;
      if (!this.authkit){
        throw new Error('authkit is null')
      }
      const user = this.authkit.getUser();
      return !!user;
    } catch (error) {
      console.debug('WorkOS isAuthenticated check failed', error);
      void this.slackService.postError(error, 'WorkOS isAuthenticated check failed', null, 'medium');
      return false;
    }
  }

  public async getUser() {
    await this.isInitialized;
    return this.authkit ? this.authkit.getUser() : { error: 'authkit init failed' };
  }

  public async getAccessToken() {
    await this.isInitialized;
    if (!this.authkit) {
      throw new Error('Cannot get token: AuthKit initialization failed');
    }
    return this.authkit.getAccessToken();
  }

  public getAccessTokenRxJS(): Observable<string> {
    return from(this.getAccessToken()).pipe(
      catchError(error => {
        this.slackService.postError(
          { 
            originalError: error,
            callStack: new Error().stack
          },
          'WorkOS getAccessToken error',
          null,
          'medium'
        );
        throw new Error('Cannot get access token');
      })
    );
  }

  public switchOrganization(organizationId: string) {
    return this.authkit.switchToOrganization({ organizationId });
  }

  public signOut() {
    if (Capacitor.isNativePlatform()) {
      this.authkit.signOut({ navigate: false, returnTo: environment.pwaOAuthRedirectUri });
    } else {
      this.authkit.signOut({ returnTo: environment.pwaOAuthRedirectUri });
    }
  }
}
